import { useCallback, useState } from 'react'

import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { useNavigation } from '@/hooks/useNavigation'
import { usePermissions } from '@/hooks/usePermissions'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd'

import { Box, useDisclosure } from '@chakra-ui/react'

import { EditFloorModal } from '@/features/floor-crud'

import { FloorI } from '../../types/types'
import { FloorListItem } from './FloorListItem'

interface FloorListIProps {
  floors: FloorI[]
  currentFloorId?: string
  onReorderFloors?: (floorIds: string[]) => void
  canReorderFloors?: boolean
}

export const FloorList = ({
  floors,
  currentFloorId,
  onReorderFloors,
  canReorderFloors = false,
}: FloorListIProps) => {
  const {
    isOpen: isEditFloorOpen,
    onClose: onEditFloorClose,
    onOpen: onEditFloorOpen,
  } = useDisclosure()

  const { shouldEnableUpdateFloor } = usePermissions()
  const isEditMode = useIsEditModeSatisfied()
  const isEditEnabled = shouldEnableUpdateFloor && isEditMode
  const { goToFloorPlan } = useNavigation()

  const [editedFloorId, setEditedFloorId] = useState(null)

  const onFloorClick = (floorId: string) => {
    goToFloorPlan(floorId)
  }

  const onEditButtonClick = (floorId: string, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent the floor click handler
    setEditedFloorId(floorId)
    onEditFloorOpen()
  }

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || !floors || !onReorderFloors) return

      const items = Array.from(floors)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      const floorIds = items.map((item) => item.id)
      onReorderFloors(floorIds)
    },
    [floors, onReorderFloors]
  )

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='floor-list'>
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {floors?.map((floor, index) => (
                <Draggable
                  draggableId={floor.id}
                  index={index}
                  isDragDisabled={!canReorderFloors}
                  key={floor.id}
                >
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      mb='10px'
                    >
                      <FloorListItem
                        dragHandleProps={
                          canReorderFloors ? provided.dragHandleProps : null
                        }
                        hasIncidents={floor.hasIncidents}
                        isCurrentFloor={currentFloorId === floor.id}
                        isDefaultFloor={floor.isDefaultFloor}
                        isEditEnabled={isEditEnabled}
                        label={floor.label}
                        onClick={() => onFloorClick(floor.id)}
                        onEditButtonClick={(event) =>
                          onEditButtonClick(floor.id, event)
                        }
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      {isEditFloorOpen && (
        <EditFloorModal
          floorId={editedFloorId}
          isOpen={isEditFloorOpen}
          onClose={onEditFloorClose}
        />
      )}
    </>
  )
}
